import { Tabs } from '@surecompanies/core_components'
import type { TabsProps } from '@surecompanies/core_components'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import Page from 'layouts/Page'
import React, { useCallback } from 'react'
import { useSearchParams } from 'hooks/useSearchParams'
import { PLAN_MANAGEMENT_V2 } from '../../paths'
import { DATA_TYPES, SUPPORTED_DATA_TYPES, getDataTypeLabel } from './constants'
import DataTypeTab from './tabs/DataTypeTab/DataTypeTab'
import GlobalOverrideFilter from '../PlanOverrides/components/GlobalOverrideFilter'

interface ReviewPlanSnapshotProps {
  params: { id: string }
}

export default function ReviewPlanSnapshot({
  params,
}: ReviewPlanSnapshotProps) {
  const taskId = params.id
  const { params: urlParams, setParams } = useSearchParams()

  const rawDataType = urlParams.dataType as string | undefined

  const activeDataType =
    rawDataType && SUPPORTED_DATA_TYPES.has(rawDataType)
      ? rawDataType
      : 'LOCATION'

  const globalState = (urlParams.state as string) || undefined
  const globalIssuerId = (urlParams.issuerId as string) || undefined
  const globalHiosPlanId = (urlParams.hiosPlanId as string) || undefined

  const handleDataTypeChange = useCallback(
    (key: string) => {
      setParams({ dataType: key, page: undefined })
    },
    [setParams]
  )

  const handleGlobalStateChange = useCallback(
    (value: string | undefined) => {
      setParams(
        { state: value, states: undefined, page: undefined },
        { replace: true }
      )
    },
    [setParams]
  )

  const handleGlobalIssuerIdChange = useCallback(
    (value: string) => {
      setParams(
        { issuerId: value || undefined, page: undefined },
        { replace: true }
      )
    },
    [setParams]
  )

  const handleGlobalHiosPlanIdChange = useCallback(
    (value: string) => {
      setParams(
        { hiosPlanId: value || undefined, page: undefined },
        { replace: true }
      )
    },
    [setParams]
  )

  const handleGlobalClearAll = useCallback(() => {
    setParams(
      {
        state: undefined,
        states: undefined,
        issuerId: undefined,
        hiosPlanId: undefined,
        page: undefined,
      },
      { replace: true }
    )
  }, [setParams])

  const tabItems: TabsProps['items'] = DATA_TYPES.map((dt) => ({
    key: dt.dataType,
    label: getDataTypeLabel(dt.dataType),
    disabled: !SUPPORTED_DATA_TYPES.has(dt.dataType),
    children: (
      <DataTypeTab
        taskId={taskId}
        dataType={dt.dataType}
        enabled={dt.enabled}
        globalState={globalState}
        globalIssuerId={globalIssuerId}
        globalHiosPlanId={globalHiosPlanId}
      />
    ),
  }))

  return (
    <Page
      maxWidth="100%"
      title="Review Plan Snapshot"
      breadcrumb={
        <BreadcrumbNav
          items={[
            { title: 'Plans', path: `/${PLAN_MANAGEMENT_V2}` },
            { title: 'Review' },
          ]}
        />
      }
    >
      <GlobalOverrideFilter
        state={globalState}
        issuerId={globalIssuerId}
        hiosPlanId={globalHiosPlanId}
        onStateChange={handleGlobalStateChange}
        onIssuerIdChange={handleGlobalIssuerIdChange}
        onHiosPlanIdChange={handleGlobalHiosPlanIdChange}
        onClearAll={handleGlobalClearAll}
      />

      <Tabs
        type="line"
        activeKey={activeDataType}
        items={tabItems}
        onChange={handleDataTypeChange}
        destroyInactiveTabPane
      />
    </Page>
  )
}
